import React, { useRef, useState, useCallback } from "react";
import { Move } from "lucide-react";

/**
 * Drag-to-reveal Before/After slider.
 * Shows AFTER behind; BEFORE clipped on top; handle at `pos` percentage.
 */
export default function BeforeAfterSlider({ before, after, alt = "", testid, aspect = "aspect-[4/3]" }) {
  const [pos, setPos] = useState(50);
  const [dragging, setDragging] = useState(false);
  const containerRef = useRef(null);

  const updateFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = clientX - rect.left;
    const next = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setPos(next);
  }, []);

  const onMove = (e) => {
    if (!dragging && e.type !== "click") return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    updateFromClientX(clientX);
  };

  const onMouseDown = (e) => {
    setDragging(true);
    updateFromClientX(e.clientX);
  };
  const onTouchStart = (e) => {
    setDragging(true);
    updateFromClientX(e.touches[0].clientX);
  };
  const onEnd = () => setDragging(false);

  return (
    <div
      ref={containerRef}
      data-testid={testid}
      onMouseDown={onMouseDown}
      onMouseMove={onMove}
      onMouseUp={onEnd}
      onMouseLeave={onEnd}
      onTouchStart={onTouchStart}
      onTouchMove={onMove}
      onTouchEnd={onEnd}
      className={`relative overflow-hidden ${aspect} bg-[#E8E0D2] cursor-ew-resize select-none group`}
    >
      <img
        src={after}
        alt={`${alt} — after`}
        className="absolute inset-0 w-full h-full object-cover"
        draggable={false}
      />
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      >
        <img
          src={before}
          alt={`${alt} — before`}
          className="absolute inset-0 w-full h-full object-cover"
          draggable={false}
        />
      </div>

      {/* labels */}
      <span className="absolute top-4 left-4 bg-[#2B2420]/85 text-[#FAF8F4] text-[10px] font-bold uppercase tracking-[0.2em] px-2.5 py-1.5">
        Before
      </span>
      <span className="absolute top-4 right-4 bg-[#C9A24C] text-[#2B2420] text-[10px] font-bold uppercase tracking-[0.2em] px-2.5 py-1.5">
        After
      </span>

      {/* divider line */}
      <div
        className="absolute inset-y-0 w-[2px] bg-[#FAF8F4]/85 pointer-events-none"
        style={{ left: `${pos}%` }}
      />
      {/* handle */}
      <div
        className="ba-handle absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-11 h-11 rounded-full bg-[#FAF8F4] border-[3px] border-[#B8732C] flex items-center justify-center pointer-events-none transition-transform group-hover:scale-110"
        style={{ left: `${pos}%` }}
      >
        <Move className="w-4 h-4 text-[#B8732C]" strokeWidth={2.5} />
      </div>
    </div>
  );
}
